import { useState } from "react";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { useFocusTrap } from "../../lib/useFocusTrap";
import styles from "./inbound.module.css";

/** A reject is irreversible for the sender, so the note is required, not optional. */
export function RejectDialog({
  invoiceNumber,
  pending,
  onClose,
  onConfirm,
}: {
  invoiceNumber: string;
  pending: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
}) {
  const box = useFocusTrap<HTMLDivElement>(onClose);
  const [note, setNote] = useState("");
  const { t } = useTranslation();

  return (
    <div className={styles.dialogWrap}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t("inbound.rejectDialog.ariaLabel", "Reject this document")}
        ref={box}
        tabIndex={-1}
      >
        <h2 className={styles.dialogTitle}>{t("inbound.rejectDialog.title", "Reject this document")}</h2>
        <p className={styles.subtle}>
          {invoiceNumber}{" "}
          {t(
            "inbound.rejectDialog.body",
            "stays archived. The note is passed back to the sender by the service provider.",
          )}
        </p>

        <label className={styles.dialogLabel} htmlFor="reject-note">
          {t("inbound.rejectDialog.reasonLabel", "Why is it rejected?")}
        </label>
        <textarea
          id="reject-note"
          className="input"
          rows={3}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />

        <div className={styles.dialogActions}>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>
            {t("inbound.rejectDialog.cancel", "Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={pending || note.trim().length === 0}
            onClick={() => onConfirm(note.trim())}
          >
            {t("inbound.rejectDialog.confirm", "Confirm rejection")}
          </button>
        </div>
      </div>
    </div>
  );
}
